import { useState } from "react";
import { SectionCard } from "../components/ui";
import { fmtDate } from "../utils";
import type { PowerProject } from "../types";
import { useControl } from "./hooks";
import { ProcessGraphView } from "./ProcessGraphView";
import { GanttView } from "./GanttView";
import { ParcelGraphView } from "./ParcelGraphView";
import type { ControlItem } from "./types";
import { HELP } from "./labels";
import { quickTargetOf, QuickTargetModal, type QuickTarget } from "./QuickTargetModal";
import {
  CourtPill, DeadlineCell, ItemCell, Segmented, StatStrip, TABLE_CLS, Td, Th, TRow,
  useTableSort, type SortAccessors, type Tone,
} from "./tableUi";

// „Czyj ruch" — mówimy wprost, kto ma coś zrobić, zamiast żargonu „piłka".
const COURT: Record<string, string> = {
  ours: "nasz ruch",
  office: "czekamy na odpowiedź",
  negotiator: "negocjator w terenie",
  // krok ruszony poza kolejnością — prereqi w DAG jeszcze niedomknięte; NIE nasz ruch
  blocked: "zablokowane",
};
// projektant/koordynator = jedna strona („my") — biznesowo bez różnicy
const ROLE: Record<string, string> = {
  designer: "My", coordinator: "My", office: "Urząd",
  negotiator: "Negocjator", investor: "Inwestor",
};
// „Etap" to w rzeczywistości kamień milowy z tablicy kamieni — nazywamy go tak samo.
const KIND: Record<string, string> = {
  milestone: "Kamień", proceeding: "Sprawa urzędowa", parcel: "Działka", location: "Działka",
};

function toneOf(i: ControlItem): Tone {
  return i.sla_breached ? "late" : i.warn ? "warn" : i.stalled ? "idle" : "ok";
}

/** Termin pozycji jednym zwrotem: „po terminie o N dni" / „zostało N dni". */
function Deadline({ i }: { i: ControlItem }) {
  const overKpa = i.breach_reason === "kpa" ? (i.kpa_days_over ?? 0) : 0;
  const daysLeft = i.days_left;
  const date = i.due_date ? fmtDate(i.due_date) : null;
  if (i.sla_breached) {
    const over = overKpa > 0 ? overKpa : daysLeft != null ? Math.max(1, -daysLeft) : null;
    return (
      <DeadlineCell
        date={date}
        tone="late"
        state={`po terminie${over != null ? ` o ${over} dn.` : ""}${i.breach_reason === "kpa" ? " (KPA)" : ""}`}
        title={i.breach_reason === "kpa"
          ? `Przekroczony ustawowy termin KPA (${i.kpa_deadline ? fmtDate(i.kpa_deadline) : "?"}) — liczony od złożenia wniosku.`
          : `Limit ${i.sla_days} dni na ten krok minął (${i.days_in_court} dni w obecnym stanie).`}
      />
    );
  }
  if (daysLeft == null) return <DeadlineCell date={date} tone="ok" state="bez terminu" />;
  return (
    <DeadlineCell
      date={date}
      tone={i.warn ? "warn" : "ok"}
      state={`zostało ${daysLeft} dn.`}
      title={`Limit ${i.sla_days} dni; w obecnym stanie od ${i.days_in_court} dni.`}
    />
  );
}

/** Pasek liczb + jedno zdanie „co jest najpilniejsze" — zamiast akapitu z badge'ami. */
function Summary({ items, dormancyDays, dormant, blockedSince }: {
  items: ControlItem[]; dormancyDays: number; dormant: boolean; blockedSince: string | null;
}) {
  const breached = items.filter((i) => i.sla_breached);
  // Kto zawinił: nasza zwłoka czy przekroczony termin ustawowy urzędu. Backend
  // liczy to samo w `summary`, ale tu i tak mamy pozycje pod ręką.
  const ours = breached.filter((i) => i.whose_court === "ours").length;
  const office = breached.length - ours;
  const warn = items.filter((i) => i.warn);
  const stalled = items.filter((i) => i.stalled);
  const top = breached[0];
  return (
    <div className="space-y-3">
      <StatStrip
        stats={[
          {
            label: "Po terminie",
            value: breached.length,
            tone: breached.length > 0 ? "late" : "ok",
            title: HELP.breached,
          },
          { label: "Nasza zwłoka", value: ours, tone: ours > 0 ? "late" : "none" },
          { label: "Po stronie urzędu", value: office, tone: office > 0 ? "warn" : "none" },
          { label: "Blisko terminu", value: warn.length, tone: warn.length > 0 ? "warn" : "none", title: HELP.warn },
          { label: "Bez ruchu", value: stalled.length, tone: stalled.length > 0 ? "idle" : "none", title: HELP.stalled },
          {
            label: "Ostatnie zdarzenie",
            value: dormancyDays === 0 ? "dzisiaj" : `${dormancyDays} dn. temu`,
            tone: dormant ? "warn" : "none",
            title: HELP.dormancy,
          },
        ]}
      />
      <div className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
        {top ? (
          <>
            Najpilniejsze: <b className="text-[var(--color-text-primary)]">{top.label}</b>
            {top.phase_label ? <> — {top.phase_label.toLowerCase()}</> : null}.
            {blockedSince ? <> Coś jest po terminie nieprzerwanie od <b>{fmtDate(blockedSince)}</b>.</> : null}
          </>
        ) : (
          <>Żadna pozycja nie przekroczyła swojego limitu czasu.</>
        )}
        {dormant && <> Projekt wygląda na uśpiony — od {dormancyDays} dni nic się nie zadziało.</>}
      </div>
    </div>
  );
}

export function ProcessControlTab({ connection }: { connection: PowerProject }) {
  const { data, isLoading } = useControl(connection.id);
  const [view, setView] = useState<"stages" | "gantt" | "parcels">("stages");
  const [target, setTarget] = useState<QuickTarget | null>(null);
  const items = data?.items ?? [];
  const accessors: SortAccessors<ControlItem> = {
    item: (i) => i.label,
    phase: (i) => i.phase_label ?? "",
    court: (i) => COURT[i.whose_court] ?? i.whose_court,
    due: (i) => i.due_date ?? null,
    role: (i) => ROLE[i.responsible_role] ?? i.responsible_role,
  };
  const { sort, toggle, sorted } = useTableSort(items, accessors);
  return (
    <div className="space-y-4 mt-4">
      <SectionCard title="Kontrola procesu — co się dzieje i kto ma ruch" bodyClassName="p-0">
        {isLoading ? (
          <div className="p-4 text-sm text-[var(--color-text-muted)]">Ładowanie…</div>
        ) : (
          <>
            <div className="p-4 pb-3">
              <Summary
                items={items}
                dormancyDays={data?.dormancy_days ?? 0}
                dormant={!!data?.summary.dormant}
                blockedSince={data?.summary.blocked_since ?? null}
              />
            </div>
            {items.length === 0 ? (
              <div className="px-4 pb-5 text-sm text-[var(--color-text-muted)]">
                ✓ Nic do pilnowania — brak otwartych pozycji.
              </div>
            ) : (
              <div>
                <table className={TABLE_CLS}>
                  <colgroup>
                    <col style={{ width: "22%" }} />
                    <col />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "8%" }} />
                  </colgroup>
                  <thead>
                    <tr className="border-y border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
                      <Th sortKey="item" sort={sort} onSort={toggle}>Pozycja</Th>
                      <Th sortKey="phase" sort={sort} onSort={toggle}>Co się teraz dzieje</Th>
                      <Th sortKey="court" sort={sort} onSort={toggle} info={HELP.whoseCourt}>Czyj ruch</Th>
                      <Th sortKey="due" sort={sort} onSort={toggle}
                        info="Dokąd jest czas na obecny krok i ile go zostało. „Po terminie” = limit minął; KPA = przekroczony termin kodeksowy urzędu (liczony od złożenia wniosku).">
                        Termin
                      </Th>
                      <Th sortKey="role" sort={sort} onSort={toggle} info={HELP.slaRole}>Kto</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((i, idx) => {
                      const t = quickTargetOf(i, connection.id);
                      return (
                      <TRow key={idx} tone={toneOf(i)}>
                        <Td>
                          <ItemCell
                            kind={KIND[i.kind] ?? i.kind}
                            label={i.label}
                            onClick={t ? () => setTarget(t) : undefined}
                          />
                        </Td>
                        <Td className="text-[var(--color-text-secondary)]">
                          <div className="leading-tight">
                            <div>{i.phase_label || "—"}</div>
                            {i.stalled && (
                              <div className="font-mono text-[10px] text-[var(--color-text-muted)] mt-0.5">
                                bez ruchu {i.days_idle} dn.
                              </div>
                            )}
                          </div>
                        </Td>
                        <Td><CourtPill court={i.whose_court} label={COURT[i.whose_court] ?? i.whose_court} /></Td>
                        <Td><Deadline i={i} /></Td>
                        <Td className="font-mono text-xs text-[var(--color-text-muted)]">
                          {ROLE[i.responsible_role] ?? i.responsible_role}
                        </Td>
                      </TRow>
                      );
                    })}
                  </tbody>
                </table>
                <p className="px-4 py-3 text-[10px] font-mono text-[var(--color-text-muted)] leading-relaxed">
                  Pokazujemy tylko pozycje, przy których teraz można coś zrobić (w toku, złożone lub gotowe do startu).
                  Kroki czekające na poprzedników widać na grafie poniżej.
                </p>
              </div>
            )}
          </>
        )}
      </SectionCard>

      <Segmented
        value={view}
        onChange={(v) => setView(v as typeof view)}
        options={[["stages", "Etapy (DAG)"], ["gantt", "Harmonogram"], ["parcels", "Działki (właściciele)"]]}
      />
      {view === "stages" && <ProcessGraphView connection={connection} />}
      {view === "gantt" && <GanttView connection={connection} />}
      {view === "parcels" && <ParcelGraphView connectionId={connection.id} active />}

      {target && <QuickTargetModal target={target} onClose={() => setTarget(null)} />}
    </div>
  );
}
